import { Assistant } from "@inrev/assistant";
import type { Code, Nodes } from "mdast";
import type { ExtraProps } from "react-markdown";
import { assistantMarkdown } from "../assistantCitations";

const CHART_LANGUAGE_CLASS = `language-${Assistant.Charts.CHART_LANGUAGE}`;

/**
 * A closing fence still streaming in, such as a lone "``". Until it has three backticks it does not
 * close the block, so it would otherwise be read as part of the JSON.
 */
const PARTIAL_CLOSING_FENCE = /\n[ \t]*(`{1,2}|~{1,2})[ \t]*\n?$/;

/** The end of the text before a block: its line breaks and the indent of the fence line. */
const LEADING_GAP = /(\r?\n[ \t]*)+$/;

/** The start of the text after a block: the rest of the fence line and blank lines, not indents. */
const TRAILING_GAP = /^[ \t]*(\r?\n)+/;

/** What may follow a chart's JSON object while its closing fence is still streaming in. */
const ONLY_FENCE_SO_FAR = /^[\s`~]*$/;

const chartBlocks = (node: Nodes): Code[] =>
	node.type === "code"
		? node.lang === Assistant.Charts.CHART_LANGUAGE
			? [node]
			: []
		: "children" in node
			? node.children.flatMap(chartBlocks)
			: [];

const chartBlocksIn = (text: string): Code[] => chartBlocks(assistantMarkdown.parse(text));

const startOf = (block: Code): number => block.position?.start.offset ?? 0;

const endOf = (block: Code): number => block.position?.end.offset ?? 0;

/** Whether the block ends with its own closing fence, rather than running to the end of the text. */
const isClosed = (text: string, block: Code): boolean => {
	const lines = text.slice(startOf(block), endOf(block)).split("\n");
	const opening = Assistant.Charts.OPENING_FENCE.exec(lines[0] ?? "");
	if (opening === null || lines.length < 2) return false;
	return Assistant.Charts.isClosingFence(lines.at(-1) ?? "", opening[1] ?? "");
};

/**
 * The text with a closing fence written after `block`'s JSON, when more than a fence follows it
 * inside the block. Markdown ends an unclosed fence at the next bare fence, or at the end of the
 * text, so a block that swallowed prose may still look closed.
 */
const closeAfterJson = (text: string, block: Code): string | undefined => {
	const lineEnd = text.indexOf("\n", startOf(block));
	const opening = Assistant.Charts.OPENING_FENCE.exec(text.slice(startOf(block)));
	if (lineEnd === -1 || opening === null) return undefined;
	const end = Assistant.Charts.jsonObjectEnd(text, lineEnd + 1);
	if (end === undefined || ONLY_FENCE_SO_FAR.test(text.slice(end, endOf(block)))) return undefined;
	// The opening fence's column, not its line's indent: in a list item the fence sits past the marker.
	const indent = " ".repeat((block.position?.start.column ?? 1) - 1);
	return `${text.slice(0, end)}\n${indent}${opening[1]}\n${text.slice(end)}`;
};

/**
 * The answer with forgotten closing fences restored. Markdown runs an unclosed fence on to the
 * next bare fence or the end of the text, so the prose and charts after it would vanish into it. A block whose JSON is still
 * incomplete, or is followed only by a fence streaming in, is left open.
 */
export const withClosedChartFences = (text: string): string => {
	for (const block of chartBlocksIn(text)) {
		const closed = closeAfterJson(text, block);
		if (closed === undefined) continue;
		// Each repair must end its block at the JSON, or the next pass would make it again.
		const start = startOf(block);
		const repaired = chartBlocksIn(closed).find((next) => startOf(next) === start);
		return repaired !== undefined && closeAfterJson(closed, repaired) === undefined
			? withClosedChartFences(closed)
			: text;
	}
	return text;
};

/** Where each chart block still being written starts: one whose closing fence has not arrived. */
export const openChartBlockStarts = (text: string): ReadonlySet<number> =>
	new Set(
		chartBlocksIn(text)
			.filter((block) => !isClosed(text, block))
			.map(startOf),
	);

/** Prose on both sides of a removed block keeps one blank line between it. */
const joinAround = (before: string, after: string): string => {
	const left = before.replace(LEADING_GAP, "");
	const right = after.replace(TRAILING_GAP, "");
	return left === "" || right === "" ? left + right : `${left}\n\n${right}`;
};

/**
 * The answer without its chart blocks, for copying out of the panel: a block is JSON, not prose.
 * Fences are found by the same markdown parse that renders them, so an indented block goes too.
 * Only the spacing next to a removed block changes.
 */
export const withoutChartBlocks = (answer: string): string => {
	const text = withClosedChartFences(answer);
	const blocks = chartBlocksIn(text);
	if (blocks.length === 0) return answer;
	const segments: string[] = [];
	let start = 0;
	for (const block of blocks) {
		segments.push(text.slice(start, block.position?.start.offset ?? start));
		start = block.position?.end.offset ?? start;
	}
	segments.push(text.slice(start));
	return segments.reduce(joinAround);
};

type MarkdownElement = NonNullable<ExtraProps["node"]>;

/** The body of a ```chart fence, read from the `pre` element react-markdown renders it into. */
export const chartBlockSource = (pre: MarkdownElement | undefined): string | undefined => {
	const code = pre?.children[0];
	if (code?.type !== "element" || code.tagName !== "code") return undefined;
	const className = code.properties.className;
	if (!Array.isArray(className) || !className.includes(CHART_LANGUAGE_CLASS)) return undefined;
	return code.children
		.map((child) => (child.type === "text" ? child.value : ""))
		.join("")
		.replace(PARTIAL_CLOSING_FENCE, "");
};
